import { assertUnreachable, isDefined } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import { CHOICE_TYPE, Choices } from '../../../types/choice';
import { ContentId } from '../../../types/content-block';
import { FormsConstants } from '../../../types/forms-constants';
import { FunctionalElement } from '../../../types/functional-element';
import {
    updateFunctionalElementsChoicesSelection,
    updateFunctionalElementSelection,
} from '../../actions/functional-elements.actions';
import { FormState } from '../../state/form.state';
import {
    resetFunctionalElement,
    setFunctionalElementToNAWithoutDisablingCheckbox,
    setFunctionalElementToNo,
    setFunctionalElementToYes,
    updateStateAfterContentChanges,
} from './form-reducer.utilities';

export function updateFunctionalElementSelectionReducer(
    state: Readonly<FormState>,
    props: { id: ContentId; selectedChoiceXmlId: string | undefined },
): Readonly<FormState> {
    const oldFunctionalElement = state.participantCopies[state.selectedParticipantCopyId].functionalElements[props.id];

    const oldChoices = oldFunctionalElement.choices;
    if (!isDefined(oldChoices)) {
        throw new Error(
            `FormReducer: updateFunctionalElementSelectionReducer: Functional element has no choices attribute!`,
        );
    }

    // if no choices are available for the functional element, then do not interfere with the state;
    if (Object.values(oldChoices).length === 0) {
        return state;
    }

    const oldSelectedChoice = Object.values(oldChoices).find((choice) => choice?.xmlId === props.selectedChoiceXmlId);

    // Build new choices object
    const newChoices: Choices = Object.keys(oldChoices).reduce((accu, key: keyof Choices) => {
        const choice = oldChoices[key];

        return {
            ...accu,
            [key]: {
                ...choice,
                selected: choice?.xmlId === props.selectedChoiceXmlId,
                selectedInMaster:
                    state.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID
                        ? choice?.xmlId === props.selectedChoiceXmlId
                        : choice?.selectedInMaster,
            },
        };
    }, {});

    // Build new functional element
    const newFunctionalElement: { [key: string]: FunctionalElement } = {};
    newFunctionalElement[props.id] = { ...oldFunctionalElement, choices: newChoices };

    const selectedChoice = Object.values(newChoices).find((choice) => choice?.xmlId === props.selectedChoiceXmlId);

    const selectedChoiceType = selectedChoice?.type;
    switch (selectedChoiceType) {
        case undefined:
            newFunctionalElement[props.id] =
                oldSelectedChoice?.type === CHOICE_TYPE.CHOICE_POSITIVE
                    ? newFunctionalElement[props.id]
                    : resetFunctionalElement(newFunctionalElement[props.id]);
            break;
        case CHOICE_TYPE.CHOICE_POSITIVE:
            newFunctionalElement[props.id] = setFunctionalElementToYes(newFunctionalElement[props.id]);
            break;
        case CHOICE_TYPE.CHOICE_NEGATIVE:
            newFunctionalElement[props.id] = setFunctionalElementToNo(newFunctionalElement[props.id]);
            break;
        case CHOICE_TYPE.CHOICE_NA:
            newFunctionalElement[props.id] = setFunctionalElementToNAWithoutDisablingCheckbox(
                newFunctionalElement[props.id],
            );
            break;
        case CHOICE_TYPE.UNKNOWN:
            throw new Error(`FormReducer: updateFunctionalElementSelectionReducer: unknown selected choice`);

        default:
            assertUnreachable(selectedChoiceType);
    }

    return updateStateAfterContentChanges(state, {
        ...state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        ...newFunctionalElement,
    });
}

export function updateFunctionalElementsChoicesSelectionReducer(
    state: Readonly<FormState>,
    props: {
        ids: ContentId[];
        selected: CHOICE_TYPE;
    },
): Readonly<FormState> {
    const functionalElements = state.participantCopies[state.selectedParticipantCopyId].functionalElements;

    return props.ids.reduce((acc, curr) => {
        const choices = functionalElements[curr].choices;

        if (isDefined(choices)) {
            const choiceToSelect = Object.values(choices).find((choice) => choice?.type === props.selected);
            if (isDefined(choiceToSelect)) {
                return updateFunctionalElementSelectionReducer(acc, {
                    id: curr,
                    selectedChoiceXmlId: choiceToSelect.xmlId,
                });
            } else {
                return acc;
            }
        } else {
            return acc;
        }
    }, state);
}

export const selectionReducer = [
    on<FormState, Array<typeof updateFunctionalElementSelection>>(
        updateFunctionalElementSelection,
        updateFunctionalElementSelectionReducer,
    ),
    on<FormState, Array<typeof updateFunctionalElementsChoicesSelection>>(
        updateFunctionalElementsChoicesSelection,
        updateFunctionalElementsChoicesSelectionReducer,
    ),
];
